import React, { createContext, useContext, useEffect } from 'react';
import { transformSuccessQuery } from '../transformSuccessQuery';
import { useSearchResponseQuery } from '../SearchResponseProvider';
import { SearchResponseContextType } from '../SearchResponseProvider/types';
import {
  PerformedSearchRequestContext,
  usePerformedSearchRequest,
} from '../SearchRequestProvider';
import itineraryResolver, {
  Extensions,
  ResolvedItinerary,
  externalSelectionResolver,
} from '../../resolvers/itinerary';
import AvailableFiltersBuilder from './AvailableFiltersBuilder';
import CheapestItineraryBuilder from '../FilteredItinerariesProvider/CheapestItineraryBuilder';
import MostExpensiveItineraryBuilder, {
  MostExpectiveItineraryBuilderReturnType,
} from '../FilteredItinerariesProvider/MostExpensiveItineraryBuilder';
import { useFeeType } from 'clientState/providers/FeeTypeProvider';
import {
  SemSearchResultContextType,
  useSemSearchResponseQuery,
} from '../SEM/SemResponseProvider';
import {
  FeeType,
  ItineraryFragment,
  SearchQueryQuery,
  SegmentFragment,
} from 'generated/graphql';
import { useUnbundle } from '../SearchResponseProvider/SearchItinerariesResponseProvider';
import { useSelectedUnbundleDepartureId } from '../SelectedUnbundleDepartureProvider';

export type ParsedSearchResponseContextType = Omit<
  SearchResponseContextType,
  'data' | 'extensions'
> & {
  data: ReturnType<typeof transformSearchResponse>;
  extensions: Extensions | null;
};
type AvailableFiltersType = ReturnType<
  ReturnType<typeof AvailableFiltersBuilder>['build']
>;
const ParsedSearchResponseContext =
  createContext<ParsedSearchResponseContextType>({
    loading: true,
    error: null,
    extensions: null,
    data: null,
  });

export const useParsedSearchResponseQuery = () =>
  useContext(ParsedSearchResponseContext);

const getFeeType = ({ fees }, feeType) => {
  return fees.find(({ type }) => type.id === feeType?.id) ?? fees[0];
};

const transformSearchResponse = (
  query: SearchResponseContextType,
  {
    searchRequest,
    feeType,
    isUnbundle,
    isDepartureSelected,
  }: {
    searchRequest: PerformedSearchRequestContext | null;
    feeType: FeeType;
    isUnbundle?: boolean;
    isDepartureSelected?: boolean;
  }
):
  | (SearchQueryQuery['search'] & {
      cheapest: ReturnType<
        ReturnType<typeof CheapestItineraryBuilder>['build']
      >;
      mostExpensive: ReturnType<
        MostExpectiveItineraryBuilderReturnType['build']
      >;
      itineraries: ResolvedItinerary[];
      externalSelection: ResolvedItinerary | null;
      currency?: string;
      availableFilters: AvailableFiltersType;
    })
  | null => {
  const { data, extensions } = query;
  if (!data || !searchRequest) return null;
  const availableFiltersBuilder = AvailableFiltersBuilder(
    data,
    searchRequest,
    isUnbundle,
    isDepartureSelected
  );
  const cheapestItineraryBuilder = CheapestItineraryBuilder();
  const mostExpensiveItineraryBuilder = MostExpensiveItineraryBuilder();
  const { search } = data;
  let externalSelection;
  const itineraries: ResolvedItinerary[] = [];
  search.itineraries.forEach((itinerary, index) => {
    availableFiltersBuilder.add(itinerary);
    cheapestItineraryBuilder.add(getFeeType(itinerary, feeType), index);
    mostExpensiveItineraryBuilder.add(getFeeType(itinerary, feeType), index);
    if (itinerary.key && itinerary.key === search.externalSelection?.id) {
      externalSelection = externalSelectionResolver(
        itinerary,
        index,
        extensions as Extensions,
        search.defaultFeeType
      );
    } else {
      itineraries.push(
        itineraryResolver(itinerary, index, extensions as Extensions)
      );
    }
  });
  const cheapest = cheapestItineraryBuilder.build();
  const mostExpensive = mostExpensiveItineraryBuilder.build();
  return {
    ...search,
    cheapest,
    mostExpensive,
    itineraries,
    externalSelection,
    currency: cheapest?.itinerary?.price.currency,
    availableFilters: availableFiltersBuilder.build(mostExpensive),
  };
};

const ParsedSearchResponseProvider = ({ children, onSearchLoaded }) => {
  const searchRequest = usePerformedSearchRequest();
  const { isUnbundle } = useUnbundle();
  const selectedDepartureId = useSelectedUnbundleDepartureId();
  const queryState = useSearchResponseQuery();
  const { data } = queryState;
  const [feeType] = useFeeType();
  const nextQueryState = transformSuccessQuery(
    queryState,
    transformSearchResponse,
    // The cast on feeType is appropriate because it will never be null
    // as long as we got a response from the back-end
    {
      searchRequest,
      feeType: feeType as FeeType,
      isUnbundle,
      isDepartureSelected: !!selectedDepartureId,
    }
  );

  useEffect(() => {
    if (data) {
      onSearchLoaded();
    }
  }, [data, onSearchLoaded]);

  return (
    <ParsedSearchResponseContext.Provider
      value={nextQueryState as ParsedSearchResponseContextType}
    >
      {children}
    </ParsedSearchResponseContext.Provider>
  );
};

const transformSemQuery = ({
  data,
}: {
  data: SemSearchResultContextType['data'];
}): SearchResponseContextType['data'] => {
  // Format the Sem itinerary into a regular results one so we can reuse
  // the provider
  const itinerariesWithId: ItineraryFragment[] =
    data?.semItinerarySearch?.itineraries.map(
      (itinerary, index) =>
        ({
          ...itinerary,
          id: index.toString(),
        } as unknown as ItineraryFragment)
    ) || [];

  return {
    search: {
      ...data?.semItinerarySearch,
      __typename: 'SearchResponse',
      searchId: 0,
      itineraries: itinerariesWithId,
      defaultFeeType: data?.semItinerarySearch?.priceTypeDisplayed,
    },
  };
};

export const SemParsedSearchResponseProvider = ({ children }) => {
  const searchRequest = usePerformedSearchRequest();
  const semQuery = useSemSearchResponseQuery();
  const queryState = transformSuccessQuery(
    {
      data: semQuery.data,
      loading: !!semQuery.loading,
      error: semQuery.error || null,
      extensions: semQuery.extensions,
    },
    transformSemQuery,
    {}
  );
  const [feeType] = useFeeType();
  const nextQueryState = transformSuccessQuery(
    queryState as SearchResponseContextType,
    transformSearchResponse,
    // The cast on feeType is appropriate because it will never be null
    // as long as we got a response from the back-end
    { searchRequest, feeType: feeType as FeeType }
  );

  return (
    <ParsedSearchResponseContext.Provider
      value={nextQueryState as ParsedSearchResponseContextType}
    >
      {children}
    </ParsedSearchResponseContext.Provider>
  );
};

const getAvailableFilters = ({ data }: ParsedSearchResponseContextType) =>
  data?.availableFilters;

export const useAvailableFiltersQuery = () => {
  const queryState = useContext(ParsedSearchResponseContext);
  return transformSuccessQuery(queryState, getAvailableFilters, {});
};

const createAvailableFilterHook = function <
  K extends keyof NonNullable<AvailableFiltersType>
>(filterType: K) {
  return (): NonNullable<AvailableFiltersType>[K] | null => {
    const { data } = useContext(ParsedSearchResponseContext);
    return (data && data.availableFilters?.[filterType]) || null;
  };
};

export const useLocationsByLeg = createAvailableFilterHook('locationsByLeg');

export const useLegLocations = (legId) => {
  const locationsByLeg = useLocationsByLeg();
  return locationsByLeg?.[legId] || null;
};
export const useAvailableDepartureTimes =
  createAvailableFilterHook('departureTimes');
export const useConnectionDurationRanges = createAvailableFilterHook(
  'connectionDurations'
);
export const useAvailableConnections = createAvailableFilterHook('connections');
export const useAvailableTransportTypes =
  createAvailableFilterHook('transportTypes');
export const useAvailableFeeTypes = createAvailableFilterHook('feeTypes');
export const useAvailablePriceRange = createAvailableFilterHook('price');
export const useAvailableAircraftModels =
  createAvailableFilterHook('aircraftModels');

export const useSemMonths = createAvailableFilterHook('semMonths');
export const useSemWeekDays = createAvailableFilterHook('semWeekDays');
export const useSemDurationRange =
  createAvailableFilterHook('semDurationRange');

export const useConnectionDurationLegRange = (legId: number) => {
  const connectionDurationRanges = useConnectionDurationRanges();
  return connectionDurationRanges && connectionDurationRanges[legId];
};

export const getItineraryDuration = (
  legs: { segments: SegmentFragment[] }[]
) => {
  if (legs.length < 2) return 0;
  const departureDate = new Date(legs[0].segments[0].sections[0].departureDate);
  const arrivalDate = new Date(
    legs[1].segments[legs[1].segments.length - 1].sections[0].departureDate
  );

  const dateOfDeparture = new Date(
    departureDate.getFullYear(),
    departureDate.getMonth(),
    departureDate.getDate()
  );
  const dateOfArrival = new Date(
    arrivalDate.getFullYear(),
    arrivalDate.getMonth(),
    arrivalDate.getDate()
  );

  const timeDiff = Math.abs(
    dateOfDeparture.getTime() - dateOfArrival.getTime()
  );
  const numberOfNights = Math.floor(timeDiff / (1000 * 3600 * 24));
  return numberOfNights;
};

export default ParsedSearchResponseProvider;
